/**
 * Activity Rail
 * 
 * Fixed 56px left rail with icon-only navigation.
 * Follows VS Code/JetBrains IDE paradigm.
 * 
 * Per spec:
 * - Position: Left edge of viewport
 * - Width: 56px (fixed)
 * - Height: Full viewport
 * - Contains icons only, no text labels
 * - No logo (logo is in top bar only)
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faCode,
    faFlask,
    faChartColumn,
    faBell,
    faUser,
    faShield,
    faBook,
    faBrain,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useUserProfile } from '../../contexts/user/hooks';
import { useDocsStore } from '../../store/docsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';

export type RailItem = 'projects' | 'editor' | 'runs' | 'results' | 'intelligence' | 'admin' | 'docs' | 'notifications' | 'profile';

interface RailIconProps {
    id: RailItem;
    icon: IconDefinition;
    label: string;
    shortcut: string;
    isActive: boolean;
    hasIndicator?: boolean;
    onClick: () => void;
}

const RailIcon: React.FC<RailIconProps> = ({ icon, label, shortcut, isActive, hasIndicator, onClick }) => {
    return (
        <button
            onClick={onClick}
            className={`
                relative group w-full h-12 flex items-center justify-center
                transition-colors duration-150
                ${isActive
                    ? 'text-[#9d4edd] bg-[#0f1923] border-l-2 border-[#9d4edd]'
                    : 'text-gray-100 hover:text-gray-300 hover:bg-white/5 border-l-2 border-transparent'
                }
            `}
            title={`${label} (${shortcut})`}
        >
            <FontAwesomeIcon icon={icon} className="h-5 w-5" />

            {/* Active project indicator dot */}
            {hasIndicator && !isActive && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-[#9d4edd] rounded-full" />
            )}

            {/* Tooltip */}
            <div className="
                absolute left-full ml-2 px-2 py-1 
                bg-[#1e1e2e] text-gray-100 text-xs rounded
                opacity-0 invisible group-hover:opacity-100 group-hover:visible
                transition-all duration-150 whitespace-nowrap z-50
                pointer-events-none shadow-lg
            ">
                {label}
                <span className="ml-2 text-gray-400">{shortcut}</span>
            </div>
        </button>
    );
};

interface ActivityRailProps {
    activeItem: RailItem | null;
    onItemClick: (item: RailItem) => void;
}

export const ActivityRail: React.FC<ActivityRailProps> = ({ activeItem, onItemClick }) => {
    const { profile } = useUserProfile();
    const location = useLocation();
    const navigate = useNavigate();
    const { isOpen: isDocsOpen, toggleDocs } = useDocsStore();
    const { filesOpen, toggleFiles, navigateToTab, currentTab } = useWorkspaceStore();
    const isAdmin = (profile as any)?.role === 'admin' || (profile as any)?.role === 'owner';

    // Check if a project is currently active (user is viewing a project)
    const hasActiveProject = /\/app\/lab\/project\/[^/]+/.test(location.pathname);

    const topItems: Array<{ id: RailItem; icon: IconDefinition; label: string; shortcut: string }> = [
        { id: 'projects', icon: faFolder, label: 'Projects', shortcut: '⌘1' },
        { id: 'editor', icon: faCode, label: hasActiveProject ? 'Code' : 'Editor', shortcut: '⌘2' },
        { id: 'runs', icon: faFlask, label: 'Analyze', shortcut: '⌘3' },
        { id: 'results', icon: faChartColumn, label: 'Results', shortcut: '⌘4' },
        { id: 'intelligence', icon: faBrain, label: 'Intelligence', shortcut: '⌘5' },
    ];

    const bottomItems: Array<{ id: RailItem; icon: IconDefinition; label: string; shortcut: string }> = [
        ...(isAdmin ? [{ id: 'admin' as RailItem, icon: faShield, label: 'Admin', shortcut: '' }] : []),
        { id: 'docs', icon: faBook, label: 'Documentation', shortcut: '?' },
        { id: 'notifications', icon: faBell, label: 'Notifications', shortcut: '' },
        { id: 'profile', icon: faUser, label: 'Profile', shortcut: '' },
    ];

    const handleItemClick = (item: RailItem) => {
        if (item === 'admin') {
            navigate('/app/admin/accounts');
        } else if (item === 'docs') {
            toggleDocs();
        } else if (hasActiveProject) {
            // When in a project, rail icons control workspace tabs directly
            switch (item) {
                case 'editor':
                    // Toggle files panel AND switch to definition tab
                    toggleFiles();
                    navigateToTab('definition');
                    break;
                case 'runs':
                    navigateToTab('backtest');
                    break;
                case 'results':
                    navigateToTab('results');
                    break;
                case 'intelligence':
                    navigateToTab('intelligence');
                    break;
                default:
                    onItemClick(item);
            }
        } else {
            onItemClick(item);
        }
    };

    // Map workspace tabs to rail items for visual feedback
    const getActiveRailItem = (): RailItem | null => {
        if (!hasActiveProject || !currentTab) return activeItem;

        switch (currentTab) {
            case 'definition':
                return 'editor';
            case 'backtest':
            case 'optimization':
            case 'wfa':
            case 'robustness':
                return 'runs';
            case 'results':
                return 'results';
            case 'intelligence':
                return 'intelligence';
            default:
                return activeItem;
        }
    };

    const activeRailItem = getActiveRailItem();

    return (
        <aside className="w-14 h-full bg-[#181825] border-r border-white/[0.06] flex flex-col flex-shrink-0">
            {/* Top-aligned items */}
            <div className="flex-1 flex flex-col pt-2">
                {topItems.map((item) => (
                    <RailIcon
                        key={item.id}
                        id={item.id}
                        icon={item.icon}
                        label={item.label}
                        shortcut={item.shortcut}
                        isActive={activeRailItem === item.id}
                        hasIndicator={item.id === 'projects' && hasActiveProject}
                        onClick={() => handleItemClick(item.id)}
                    />
                ))}
            </div>

            {/* Bottom-aligned items */}
            <div className="flex flex-col pb-2">
                {bottomItems.map((item) => (
                    <RailIcon
                        key={item.id}
                        id={item.id}
                        icon={item.icon}
                        label={item.label}
                        shortcut={item.shortcut}
                        isActive={item.id === 'docs' ? isDocsOpen : activeRailItem === item.id}
                        onClick={() => handleItemClick(item.id)}
                    />
                ))}
            </div>
        </aside>
    );
};

export default ActivityRail;
